import type { PortalTokens } from './generated/types.gen';

import { env } from '@/env';

import { authEvents } from './auth-events';
import { ApiError, unwrap, type Envelope } from './envelope';
import { tokenStore, type Tokens } from './token-store';

/**
 * Exchanges the refresh token for a new pair of tokens.
 *
 * The backend ROTATES the refresh token: each successful refresh invalidates the
 * one that was used. That makes concurrent refreshes actively harmful — if five
 * queries all get a 401 at the same moment and each starts its own refresh, the
 * second one presents a token the server has already retired and the whole
 * session is killed. The user is then signed out for no reason.
 *
 * So refreshes are funnelled through a single call:
 *   - within one tab, by sharing one in-flight promise;
 *   - across tabs, by taking a Web Lock (a browser feature that lets tabs on the
 *     same site take turns) and re-reading the store once the lock is held, so
 *     the loser of a race picks up the winner's new tokens instead of asking for
 *     another pair.
 */

const REFRESH_LOCK = 'activistarc-portal-auth-refresh';

/** Exported so a test can assert the request goes here, and so the list of
 *  paths that must never trigger a refresh can name it. */
export const REFRESH_ENDPOINT = '/activist-arc/organization/auth/refresh';

let inFlight: Promise<Tokens> | null = null;

async function requestNewTokens(): Promise<Tokens> {
  const current = tokenStore.get();
  if (!current) {
    throw new ApiError(401, null, 'There is no refresh token to use.');
  }

  // Deliberately a plain fetch rather than the generated client: the generated
  // client's fetch is the very thing that calls this, so going through it would
  // loop forever.
  //
  // The PORTAL endpoint. This used to post to /activist-arc/auth/access-token,
  // which is the mobile app's: it looks the identifier up in the app's user
  // table, and a portal identifier is in a different table, so it answered
  // "user not found" every time. Renewal could never succeed, and thirty
  // minutes after signing in the next click threw the person back to the
  // sign-in screen. The test that covered this counted requests without ever
  // checking where they went, so nothing caught it.
  const response = await fetch(`${env.apiBaseUrl}${REFRESH_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: current.refreshToken }),
  });

  const body = (await response.json().catch(() => undefined)) as
    Envelope<PortalTokens> | undefined;

  if (!response.ok) {
    throw new ApiError(response.status, body?.error ?? null, body?.message ?? null);
  }

  const data = unwrap(body, response.status);
  const next: Tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
  // Store both before releasing the lock, so any waiting caller sees the pair.
  tokenStore.set(next);
  return next;
}

/**
 * @param usedAccessToken the token the failed request actually carried.
 *
 * It matters which token this is. Reading the store here instead would read it
 * at the moment of the call, not at the moment of the failure - so with two
 * requests both sent with T1, the first refreshes to T2, and the second then
 * reads T2 from the store, decides nothing has changed since "its" token, and
 * refreshes again. Every slow response cost a rotation for nothing, which is
 * exactly what the guard exists to prevent.
 */
export async function refreshTokens(usedAccessToken?: string): Promise<Tokens> {
  const stale = usedAccessToken ?? tokenStore.get()?.accessToken;

  const runGuarded = async (): Promise<Tokens> => {
    // Somebody else may have refreshed while this caller waited for the lock.
    const latest = tokenStore.get();
    if (latest && latest.accessToken !== stale) {
      return latest;
    }
    return requestNewTokens();
  };

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(REFRESH_LOCK, runGuarded);
  }

  // No Web Locks (older browsers, and the test environment): fall back to
  // sharing one promise within this tab.
  inFlight ??= runGuarded().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Clears the session and tells the React layer to react to it. */
export function endSession(reason: 'revoked' | 'expired' | 'manual'): void {
  tokenStore.clear();
  authEvents.emitSignedOut(reason);
}
